import { Chip, Grid, MenuItem, Omit, Paper, Tooltip, Typography } from "@material-ui/core";
import { createStyles, emphasize, makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import TextField, { BaseTextFieldProps } from "@material-ui/core/TextField";
import { Cancel } from "@material-ui/icons";
import clsx from "clsx";
import React, { CSSProperties, HTMLAttributes } from "react";
import Select from "react-select";
import { ValueContainerProps } from "react-select/src/components/containers";
import { ControlProps } from "react-select/src/components/Control";
import { MenuProps, NoticeProps } from "react-select/src/components/Menu";
import { MultiValueProps } from "react-select/src/components/MultiValue";
import { OptionProps } from "react-select/src/components/Option";
import { PlaceholderProps } from "react-select/src/components/Placeholder";
import { SingleValueProps } from "react-select/src/components/SingleValue";
import { ActionMeta, ValueType } from "react-select/src/types";

type columnCount = boolean | "auto" | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | undefined;

const flaggedColor = "#fff0d1";

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      height: 250,
      minWidth: 290,
      cursor: "pointer",
    },
    input: {
      display: "flex",
      padding: 0,
      height: "auto",
      cursor: "pointer",
    },
    valueContainer: {
      display: "flex",
      flexWrap: "wrap",
      flex: 1,
      alignItems: "center",
      overflow: "hidden",
    },
    chip: {
      margin: theme.spacing(0.5, 0.25),
      maxWidth: "calc(100% - 2px)",
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === "light" ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08
      ),
    },
    noOptionsMessage: {
      padding: theme.spacing(1, 2),
    },
    singleValue: {
      fontSize: 16,
    },
    placeholder: {
      position: "absolute",
      left: 2,
      bottom: 6,
      fontSize: 16,
    },
    paper: {
      position: "absolute",
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    divider: {
      height: theme.spacing(2),
    },
    grid: {
      paddingTop: "10px",
      paddingBottom: "0px",
    },
    select: { paddingTop: 10, paddingBottom: 0 },
  })
);

export interface OptionType {
  label: React.ReactNode;
  value: string;
  canton?: string;
  tooltip?: string;
  payload?: any;
  flagged?: boolean;
  flaggedTooltip?: string;
}

function NoOptionsMessage(props: NoticeProps<OptionType>) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

type InputComponentProps = Pick<BaseTextFieldProps, "inputRef"> & HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: ControlProps<OptionType>) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

function Option(props: OptionProps<OptionType>) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

type MuiPlaceholderProps = Omit<PlaceholderProps<OptionType>, "innerProps"> &
  Partial<Pick<PlaceholderProps<OptionType>, "innerProps">>;
function Placeholder(props: MuiPlaceholderProps) {
  const { selectProps, innerProps = {}, children } = props;
  return (
    <Typography color="textSecondary" className={selectProps.classes.placeholder} {...innerProps}>
      {children}
    </Typography>
  );
}

function SingleValue(props: SingleValueProps<OptionType>) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props: ValueContainerProps<OptionType>) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: MultiValueProps<OptionType>) {
  let color: string = "";
  let tooltip: React.ReactNode;

  if (props.data.flagged) {
    color = flaggedColor;
    tooltip = props.data.flaggedTooltip;
  }

  if (props.data.tooltip) {
    tooltip = props.data.tooltip;
  } else if (props.data.label) {
    tooltip = props.data.label;
  }

  const chip = (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      style={{ backgroundColor: color }}
      deleteIcon={<Cancel {...props.removeProps} />}
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{chip}</Tooltip>;
  } else {
    return chip;
  }
}

const Menu = (props: MenuProps<OptionType>) => {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
};

export const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

type MenuPlacementType = "auto" | "bottom" | "top" | undefined;

interface CustomSelectProps {
  name: string;
  label: React.ReactNode;
  placeholder?: React.ReactNode;
  isClearable?: boolean;
  onChange?: (value: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => void;
  value?: ValueType<OptionType>;
  options?: OptionType[];
  isMulti?: boolean;
  isDisabled?: boolean;
  xs?: columnCount;
  sm?: columnCount;
  md?: columnCount;
  lg?: columnCount;
  xl?: columnCount;
  required?: boolean;
  className?: string;
  error?: boolean;
  menuPlacement?: MenuPlacementType;
  useDocumentBodyAsPortalTarget?: boolean;
}

// TODO: replace with CustomAutocomplete
const CustomSelect: React.FC<CustomSelectProps> = (props) => {
  // As react-select has some problems to scroll the menu when menuPortalTarget is document.body (needed when the select is in a table or in a popup),
  // we select mainElement and force to close the select menu when the main element is scrolled.
  const mainElement = document.querySelector("main");

  const classes = useStyles(props);
  const theme = useTheme();
  const { xs, sm, md, lg, xl, error } = props;

  const selectStyles = {
    input: (base: CSSProperties) => ({
      ...base,
      zIndex: 9999,
      color: theme.palette.text.primary,
      "& input": {
        font: "inherit",
      },
    }),
  };
  return (
    <Grid item={true} xs={xs} sm={sm} md={md} lg={lg} xl={xl} className={props.className || classes.grid}>
      <Select
        isClearable={props.isClearable}
        classes={classes}
        styles={selectStyles}
        inputId={props.name}
        menuPlacement={props.menuPlacement}
        TextFieldProps={{
          label: props.label,
          InputLabelProps: {
            htmlFor: props.name,
            shrink: true,
          },
          required: props.required,
          error: error,
        }}
        placeholder={props.placeholder}
        options={props.options}
        components={components}
        value={props.value}
        onChange={props.onChange}
        isMulti={props.isMulti}
        isDisabled={props.isDisabled}
        // eslint-disable-next-line no-mixed-operators
        menuPortalTarget={(props.useDocumentBodyAsPortalTarget && document.body) || null}
        closeMenuOnScroll={(e) => props.useDocumentBodyAsPortalTarget && e.target === mainElement}
      />
    </Grid>
  );
};

CustomSelect.defaultProps = {
  onChange: () => {},
  menuPlacement: "auto",
} as Partial<CustomSelectProps>;

export default CustomSelect;
